import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme,
  Collapse,
  TextField,
  MenuItem,
  Divider,
  Avatar,
  Badge,
  Alert,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Add,
  Reply,
  Delete,
  Archive,
  Flag,
  MoreVert,
  ExpandMore,
  ExpandLess,
  Email,
  Check,
  FilterList,
  Sort,
} from '@mui/icons-material';

/**
 * Professional Unified Inbox Manager
 * Manage all received emails, threads, and replies in one place
 */
const InboxManager = ({
  emails = [],
  threads = [],
  unreadCount = 0,
  onReplyEmail = () => {},
  onDeleteEmail = () => {},
  onArchiveEmail = () => {},
  onMarkAsRead = () => {},
  onFlagEmail = () => {},
  onSearchEmails = () => {},
  selectedEmailId = null,
  onSelectEmail = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  const filters = [
    { value: 'all', label: '📥 All', icon: 'Inbox' },
    { value: 'unread', label: '🔔 Unread', icon: 'Notification' },
    { value: 'flagged', label: '🚩 Flagged', icon: 'Flag' },
    { value: 'archive', label: '📦 Archive', icon: 'Archive' },
    { value: 'spam', label: '🚫 Spam', icon: 'Block' },
  ];

  // Filter emails
  const filteredEmails = emails.filter((email) => {
    if (searchTerm && !email.subject?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterType === 'unread') return !email.read;
    if (filterType === 'flagged') return email.flagged;
    if (filterType === 'archive') return email.archived;
    if (filterType === 'spam') return email.spam;
    return filterType === 'all';
  });

  // Sort emails
  const sortedEmails = [...filteredEmails].sort((a, b) => {
    if (sortBy === 'date') return new Date(b.date) - new Date(a.date);
    if (sortBy === 'sender') return (a.from || '').localeCompare(b.from || '');
    if (sortBy === 'subject') return (a.subject || '').localeCompare(b.subject || '');
    return 0;
  });

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (name) => {
    return (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* Header */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                    📬 Unified Inbox
                  </Typography>
                  {unreadCount > 0 && (
                    <Badge
                      badgeContent={unreadCount}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#f44336',
                          color: '#fff',
                        },
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Manage all incoming emails and conversations
                </Typography>
              </Box>
              {selectedEmails.length > 0 && (
                <Chip label={`${selectedEmails.length} selected`} onDelete={() => setSelectedEmails([])} />
              )}
            </Stack>

            {/* Search & Filters */}
            <Stack spacing={1.5}>
              <TextField
                size="small"
                placeholder="Search emails..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />

              {/* Filter & Sort Buttons */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ overflowX: 'auto', pb: 0.5 }}
                alignItems="center"
              >
                {filters.map((filter) => (
                  <Chip
                    key={filter.value}
                    size="small"
                    label={filter.label}
                    onClick={() => setFilterType(filter.value)}
                    variant={filterType === filter.value ? 'filled' : 'outlined'}
                    sx={{
                      backgroundColor:
                        filterType === filter.value ? 'rgba(102,126,234,0.2)' : undefined,
                      whiteSpace: 'nowrap',
                    }}
                  />
                ))}
                <Divider orientation="vertical" flexItem />
                <TextField
                  select
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="date">📅 Newest</MenuItem>
                  <MenuItem value="sender">👤 Sender</MenuItem>
                  <MenuItem value="subject">📄 Subject</MenuItem>
                </TextField>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Emails List */}
      {sortedEmails.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'No emails match your search' : 'Your inbox is empty'}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {sortedEmails.map((email) => {
            const isSelected = selectedEmailId === email.id;
            const isCheckboxSelected = selectedEmails.includes(email.id);

            return (
              <Card
                key={email.id}
                variant="outlined"
                onClick={() => onSelectEmail(email.id)}
                sx={{
                  cursor: 'pointer',
                  borderColor: isSelected ? '#667eea' : undefined,
                  backgroundColor: isSelected ? 'rgba(102,126,234,0.05)' : undefined,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    backgroundColor: 'rgba(102,126,234,0.02)',
                  },
                }}
              >
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    {/* Checkbox */}
                    <Checkbox
                      size="small"
                      checked={isCheckboxSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (isCheckboxSelected) {
                          setSelectedEmails(selectedEmails.filter((id) => id !== email.id));
                        } else {
                          setSelectedEmails([...selectedEmails, email.id]);
                        }
                      }}
                    />

                    {/* Avatar */}
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: '#667eea',
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(email.from)}
                    </Avatar>

                    {/* Email Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        mb={0.5}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography
                              fontWeight={email.read ? 600 : 700}
                              variant="body2"
                              sx={{
                                color: email.read ? 'text.primary' : '#667eea',
                              }}
                            >
                              {email.from || 'Unknown'}
                            </Typography>
                            {!email.read && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: '#667eea',
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            flexShrink: 0,
                            ml: { xs: 0, sm: 1 },
                          }}
                        >
                          {formatDate(email.date)}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          color: email.read ? 'text.secondary' : 'text.primary',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          mb: 0.5,
                        }}
                      >
                        {email.subject || '(No subject)'}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          display: 'block',
                        }}
                      >
                        {email.preview || email.body}
                      </Typography>

                      {/* Tags/Labels */}
                      {(email.labels || []).length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                          {email.labels.map((label, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={label}
                              variant="outlined"
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Flag & Actions */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title={email.flagged ? 'Unflag' : 'Flag'}>
                        <IconButton
                          size="small"
                          onClick={() => onFlagEmail(email.id)}
                          sx={{
                            color: email.flagged ? '#FF9800' : 'inherit',
                          }}
                        >
                          <Flag sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More options">
                        <IconButton size="small">
                          <MoreVert sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Expanded Email View */}
                  {isSelected && (
                    <Stack spacing={1.5} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #eee' }}>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        <Typography variant="body2">{email.body}</Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Reply />}
                          onClick={() => onReplyEmail(email.id)}
                          fullWidth={isMobile}
                        >
                          Reply
                        </Button>
                        {!email.read && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Check />}
                            onClick={() => onMarkAsRead(email.id)}
                            fullWidth={isMobile}
                          >
                            Mark as Read
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Archive />}
                          onClick={() => onArchiveEmail(email.id)}
                          fullWidth={isMobile}
                        >
                          Archive
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => onDeleteEmail(email.id)}
                          fullWidth={isMobile}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default InboxManager;
